import React from 'react';
import { CheckCircle2, Clock, Undo2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getRegistryState,
  REGISTRY_STATE_LABELS,
  REGISTRY_STATE_TOOLTIPS,
  type RegistryDomain,
  type RegistryState,
} from '@/lib/governance/consolidation';

interface RegistryStateChipProps {
  /** Registro a classificar (campos já carregados nas queries existentes) */
  record: Record<string, any> | null | undefined;
  /** Domínio do registro */
  domain: RegistryDomain;
  /** Permite forçar o estado, ignorando a derivação automática */
  forceState?: RegistryState;
  /** Compact mode: somente ícone (sem rótulo de texto) */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Chip discreto que comunica o estado de governança do registro.
 * Apenas três estados possíveis: consolidado / em_curso / devolvido.
 * Não expõe identificadores técnicos. Não dispara queries.
 */
export function RegistryStateChip({
  record,
  domain,
  forceState,
  iconOnly = false,
  className = '',
}: RegistryStateChipProps) {
  const state: RegistryState = forceState ?? getRegistryState(record, domain);
  const label = REGISTRY_STATE_LABELS[state];
  const tip = REGISTRY_STATE_TOOLTIPS[state];

  const config = {
    consolidado: {
      Icon: CheckCircle2,
      classes:
        'border-emerald-200 text-emerald-700 bg-emerald-50/40 dark:border-emerald-900/60 dark:text-emerald-300 dark:bg-emerald-950/20',
    },
    em_curso: {
      Icon: Clock,
      classes:
        'border-border text-muted-foreground bg-muted/40',
    },
    devolvido: {
      Icon: Undo2,
      classes:
        'border-amber-200 text-amber-700 bg-amber-50/40 dark:border-amber-900/60 dark:text-amber-300 dark:bg-amber-950/20',
    },
  }[state];

  const Icon = config.Icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] leading-none font-medium cursor-default select-none ${config.classes} ${className}`}
        >
          <Icon className="h-3 w-3" strokeWidth={2} />
          {!iconOnly && <span>{label}</span>}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs">
        <p className="font-medium mb-0.5">{label}</p>
        <p className="text-muted-foreground">{tip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export default RegistryStateChip;
